import type { QueryKey } from '@tanstack/react-query';
import { get } from '../../../redux/api';
import { useQuery } from '../../../ReactQuery';
import { window } from '../../../globals';
import type { Permission } from '../../../../typings/permissions';
import type { WindowUser } from '../../../globals';

const STALE_TIME = 1000 * 60 * 1; // 1 minute

function getPermissions() {
  return get<Permission[]>('/api/users/me/permissions');
}

const queryKey: QueryKey = ['users', 'permissions'];

/**
 * Hook which returns the current user's permissions.
 * Will stop refetching if the user is not logged in,
 * and caches the data for a bit longer than the default,
 * because this hook is used all throughout the app, and the users
 * permissions aren't changing that frequently.
 */
export default function usePermissions() {
  return useQuery<Permission[]>({
    queryKey,
    queryFn: getPermissions,
    //  Stop trying to fetch if the user is not logged in
    enabled: !!window.user,
    //  Keep the data around for a bit longer since the user permissions don't change that often
    staleTime: STALE_TIME,
    //  This keeps the window.user.permissions in sync with what we've fetched. This is helpful until we remove window.user.permissions.
    //  NOTE: Using onSuccess like this in a useQuery is an anti-pattern, instead this should probably useEffect. This is fine for now because this is short-lived code.
    onSuccess: (permissions: Permission[]) => {
      window.user = {
        ...(window.user || {}),
        permissions,
      } as WindowUser;
    },
  });
}
